import type { announceById } from "~/utils/interface/announce";

export const getUniquePromotedAnnouncements = (
  promoted: announceById[],
  excludeIds?: Set<string>
) => {
  if (!promoted.length) return [];
  const seen = new Set<string>();
  return promoted.filter((item) => {
    const id = String(item.id);
    if (seen.has(id) || (excludeIds && excludeIds.has(id))) return false;
    seen.add(id);
    return true;
  });
};

export const isPromotedAnnouncement = (
  announcement?: Partial<announceById> | null
) => {
  if (!announcement || typeof announcement !== "object") return false;
  if (announcement?.promotion?.isPromoted === true) return true;
  if (announcement?.isPromoted === true) return true;
  return announcement?.promoted?.status === "active";
};

export const removePromotedFromSearchResults = (announcements: announceById[]) => {
  if (!Array.isArray(announcements) || !announcements.length) return [];
  return announcements.filter((item) => !isPromotedAnnouncement(item));
};

export const removePromotedFromNormal = (
  normal: announceById[],
  promoted: announceById[]
) => {
  if (!normal.length || !promoted.length) return normal;
  const promotedIds = new Set(promoted.map((item) => String(item.id)));
  return normal.filter((item) => !promotedIds.has(String(item.id)));
};

export const countPromotedInNormal = (
  normal: announceById[],
  promoted: announceById[]
) => {
  if (!normal.length || !promoted.length) return 0;
  const promotedIds = new Set(promoted.map((item) => String(item.id)));
  let count = 0;
  for (const item of normal) {
    if (promotedIds.has(String(item.id))) {
      count += 1;
    }
  }
  return count;
};

export const getPromotedDisplayAnnouncements = (
  promoted: announceById[],
  maxPromoted?: number
) => {
  const uniquePromoted = getUniquePromotedAnnouncements(promoted);
  if (typeof maxPromoted !== "number") return uniquePromoted;
  if (maxPromoted <= 0) return [];
  return uniquePromoted.slice(0, maxPromoted);
};

const dedupeAnnouncementsById = (items: announceById[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = String(item.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const getDisplayedPromotedIds = (
  promoted: announceById[],
  currentSearchResults: announceById[] = []
) => {
  const seen = new Set<number>();
  const collectIds = (items: announceById[], promotedOnly: boolean) => {
    if (!Array.isArray(items) || !items.length) return;
    for (const item of items) {
      if (promotedOnly && !isPromotedAnnouncement(item)) continue;
      const id = Number(item?.id);
      if (!Number.isFinite(id)) continue;
      seen.add(id);
    }
  };

  collectIds(promoted, false);
  collectIds(currentSearchResults, true);

  return Array.from(seen);
};

export const mergePromotedAnnouncements = (
  normal: announceById[],
  promoted: announceById[],
  normalBatchSize: number,
  promotedPerBatch = 1,
  placementMode: "default" | "alternating-two-normal" = "default"
) => {
  const uniquePromoted = getUniquePromotedAnnouncements(promoted);
  const cleanedNormal = removePromotedFromNormal(normal, uniquePromoted);
  if (!cleanedNormal.length) {
    return uniquePromoted;
  }
  if (!uniquePromoted.length) return dedupeAnnouncementsById(cleanedNormal);
  const batchSize = Math.max(1, normalBatchSize);
  const promotedBatchSize = Math.max(1, promotedPerBatch);
  const merged: announceById[] = [];
  let normalIndex = 0;
  let promotedIndex = 0;

  while (normalIndex < cleanedNormal.length) {
    const normalBatch = cleanedNormal.slice(normalIndex, normalIndex + batchSize);
    normalIndex += normalBatch.length;

    const promotedBatch = uniquePromoted.slice(
      promotedIndex,
      promotedIndex + promotedBatchSize
    );
    promotedIndex += promotedBatch.length;

    if (!promotedBatch.length) {
      merged.push(...normalBatch);
      continue;
    }

    if (placementMode === "alternating-two-normal") {
      let normalPointer = 0;
      for (const promotedItem of promotedBatch) {
        merged.push(promotedItem);
        const normalChunk = normalBatch.slice(normalPointer, normalPointer + 2);
        merged.push(...normalChunk);
        normalPointer += normalChunk.length;
      }
      if (normalPointer < normalBatch.length) {
        merged.push(...normalBatch.slice(normalPointer));
      }
      continue;
    }

    // Default placement: promoted, one normal, promoted, then remaining normals.
    const [firstNormal, ...restNormals] = normalBatch;
    merged.push(promotedBatch[0]);
    if (firstNormal) {
      merged.push(firstNormal);
    }
    if (promotedBatch[1]) {
      merged.push(promotedBatch[1]);
    }
    if (promotedBatch.length > 2) {
      merged.push(...promotedBatch.slice(2));
    }
    merged.push(...restNormals);
  }

  return dedupeAnnouncementsById(merged);
};
